import { Injectable, signal } from '@angular/core';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  // Signal para almacenar las notificaciones activas
  notifications = signal<Notification[]>([]);

  /**
   * Muestra una notificación de éxito
   */
  success(title: string, message: string = '', duration: number = 5000) {
    this.show('success', title, message, duration);
  }

  /**
   * Muestra una notificación de error
   */
  error(title: string, message: string = '', duration: number = 7000) {
    this.show('error', title, message, duration);
  }

  /**
   * Muestra una notificación de advertencia
   */
  warning(title: string, message: string = '', duration: number = 5000) {
    this.show('warning', title, message, duration);
  }

  /**
   * Muestra una notificación informativa
   */
  info(title: string, message: string = '', duration: number = 5000) {
    this.show('info', title, message, duration);
  }

  /**
   * Muestra una notificación genérica
   */
  private show(type: NotificationType, title: string, message: string, duration: number) {
    const notification: Notification = {
      id: this.generateId(),
      type,
      title,
      message,
      duration,
      timestamp: new Date()
    };

    // Agregar la notificación
    this.notifications.update(notifications => [...notifications, notification]);

    // Auto-remover después de la duración especificada
    if (duration > 0) {
      setTimeout(() => {
        this.remove(notification.id);
      }, duration);
    }
  }

  /**
   * Remueve una notificación por ID
   */
  remove(id: string) {
    this.notifications.update(notifications => 
      notifications.filter(n => n.id !== id)
    );
  }

  /**
   * Limpia todas las notificaciones
   */
  clear() {
    this.notifications.set([]);
  }

  /**
   * Genera un ID único para cada notificación
   */
  private generateId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

